import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const SiteTesting = () => {
  const { hasRole, currentUser, userProfile } = useAuth();
  const [testResults, setTestResults] = useState({});

  const isAdmin = hasRole('admin');

  const testSections = [
    {
      id: 'authentication',
      title: '🔐 Authentication & User Management',
      tests: [
        { id: 'login', name: 'Login Functionality', url: '/login', status: 'pending' },
        { id: 'signup', name: 'User Registration', url: '/signup', status: 'pending' },
        { id: 'admin-approval', name: 'Admin Approval Process', url: '/admin/approval', status: 'pending' },
        { id: 'profile', name: 'User Profile Management', url: '/profile', status: 'pending' }
      ]
    },
    {
      id: 'navigation',
      title: '🧭 Navigation & Routing',
      tests: [
        { id: 'home', name: 'Home Page', url: '/', status: 'pending' },
        { id: 'about', name: 'About Pages', url: '/about/our-story', status: 'pending' },
        { id: 'events', name: 'Events Page', url: '/events', status: 'pending' },
        { id: 'news', name: 'News Page', url: '/news', status: 'pending' },
        { id: 'education', name: 'Education Pages', url: '/education/workshops', status: 'pending' }
      ]
    },
    {
      id: 'member-features',
      title: '👥 Member Features (Requires Login)',
      tests: [
        { id: 'directory', name: 'Member Directory', url: '/directory', status: 'pending' },
        { id: 'groups', name: 'Groups System', url: '/groups', status: 'pending' },
        { id: 'job-board', name: 'Job Board', url: '/jobs', status: 'pending' },
        { id: 'project-board', name: 'Project Board', url: '/projects', status: 'pending' },
        { id: 'dashboard', name: 'Member Dashboard', url: '/dashboard', status: 'pending' }
      ]
    },
    {
      id: 'admin-features',
      title: '⚙️ Admin Features (Admin Only)',
      tests: [
        { id: 'admin-dashboard', name: 'Admin Dashboard', url: '/admin', status: 'pending' },
        { id: 'data-seeder', name: 'Data Seeding', url: '/admin/seed-data', status: 'pending' },
        { id: 'events-migration', name: 'Events Migration', url: '/admin/migrate-events', status: 'pending' },
        { id: 'news-migration', name: 'News Migration', url: '/admin/migrate-news', status: 'pending' },
        { id: 'firebase-test', name: 'Firebase Testing', url: '/admin/test-firebase', status: 'pending' }
      ]
    },
    {
      id: 'data-integration',
      title: '🔥 Firebase Data Integration',
      tests: [
        { id: 'events-firebase', name: 'Events from Firebase', url: '/events', status: 'pending' },
        { id: 'news-firebase', name: 'News from Firebase', url: '/news', status: 'pending' },
        { id: 'groups-firebase', name: 'Groups from Firebase', url: '/groups', status: 'pending' },
        { id: 'jobs-firebase', name: 'Jobs from Firebase', url: '/jobs', status: 'pending' },
        { id: 'projects-firebase', name: 'Projects from Firebase', url: '/projects', status: 'pending' }
      ]
    }
  ];

  const updateTestStatus = (sectionId, testId, status) => {
    setTestResults(prev => ({
      ...prev,
      [`${sectionId}-${testId}`]: status
    }));
  };

  const getTestStatus = (sectionId, testId) => {
    return testResults[`${sectionId}-${testId}`] || 'pending';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pass': return 'text-green-600 bg-green-100';
      case 'fail': return 'text-red-600 bg-red-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pass': return '✅';
      case 'fail': return '❌';
      case 'warning': return '⚠️';
      default: return '⏳';
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">🧪 APN Site Testing Dashboard</h1>
        
        {/* Current User Status */}
        <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <h2 className="text-lg font-semibold text-blue-900 mb-2">Current User Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-blue-800">Email:</span>
              <span className="ml-2 text-blue-700">{currentUser?.email || 'Not logged in'}</span>
            </div>
            <div>
              <span className="font-medium text-blue-800">Role:</span>
              <span className="ml-2 text-blue-700">{userProfile?.role || 'None'}</span>
            </div>
            <div>
              <span className="font-medium text-blue-800">Status:</span>
              <span className="ml-2 text-blue-700">{userProfile?.status || 'None'}</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link
              to="/admin-setup"
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition duration-200 text-center text-sm"
            >
              Admin Setup Guide
            </Link>
            <Link
              to="/admin/migrate-events"
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200 text-center text-sm"
            >
              Migrate Events
            </Link>
            <Link
              to="/admin/migrate-news"
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition duration-200 text-center text-sm"
            >
              Migrate News
            </Link>
            <Link
              to="/admin/seed-data"
              className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition duration-200 text-center text-sm"
            >
              Seed Data
            </Link>
          </div>
        </div>

        {/* Test Sections */}
        <div className="space-y-8">
          {testSections.map(section => (
            <div key={section.id} className="border border-gray-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{section.title}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {section.tests.map(test => {
                  const status = getTestStatus(section.id, test.id);
                  return (
                    <div key={test.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-gray-900">{test.name}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(status)}`}>
                          {getStatusIcon(status)} {status}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Link
                          to={test.url}
                          className="text-blue-600 hover:text-blue-800 text-sm underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Test Page
                        </Link>
                        <div className="flex space-x-1">
                          <button
                            onClick={() => updateTestStatus(section.id, test.id, 'pass')}
                            className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs hover:bg-green-200"
                          >
                            ✅ Pass
                          </button>
                          <button
                            onClick={() => updateTestStatus(section.id, test.id, 'fail')}
                            className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
                          >
                            ❌ Fail
                          </button>
                          <button
                            onClick={() => updateTestStatus(section.id, test.id, 'warning')}
                            className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs hover:bg-yellow-200"
                          >
                            ⚠️ Issue
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Testing Instructions */}
        <div className="mt-8 bg-gray-50 border border-gray-200 rounded-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Testing Instructions</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Start by setting up admin credentials using the Admin Setup Guide</li>
            <li>Run the Events and News migrations to populate Firebase with data</li>
            <li>Test each page by clicking "Test Page" and verify functionality</li>
            <li>Mark each test as Pass (✅), Fail (❌), or Issue (⚠️) based on results</li>
            <li>For member features, ensure you're logged in with an approved account</li>
            <li>For admin features, ensure you have admin role privileges</li>
            <li>Check that Firebase data loads correctly (look for "🔥 Live Data" indicators)</li>
            <li>Test navigation, forms, and user interactions on each page</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default SiteTesting;
